import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, Clock, Zap, Check, X, Send, Bot, Loader2 } from 'lucide-react';
import { toast } from '../components/ui/sonner';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RequestModal = ({ agent, onClose }) => {
  const [form, setForm] = useState({ client_id: '', contact_name: '', contact_wa: '', note: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [payUrl, setPayUrl] = useState(null);

  const submit = async () => {
    if (!form.contact_name.trim() || !form.contact_wa.trim()) { toast.error('Nombre y WhatsApp son obligatorios'); return; }
    try {
      setSending(true);
      const r = await fetch(`${API}/studio/catalog/${agent.service_id}/request-install`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: form.client_id.trim() || `lead_${Date.now()}`,
          contact_name: form.contact_name.trim(), contact_wa: form.contact_wa.trim(), note: form.note.trim(),
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'No se pudo enviar');
      setDone(j.message || 'Solicitud recibida.');
      setPayUrl(j.payment_link?.url || null);
      toast.success('Solicitud enviada');
    } catch (e) { toast.error(e.message); } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="catalog-request-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0c1220] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300">Solicitar instalación</p>
            <h3 className="mt-1 text-lg font-bold text-white">{agent.name}</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="catalog-modal-close" className="rounded-full p-1 text-white/50 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        {done ? (
          <div className="mt-6 text-center" data-testid="catalog-request-success">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300"><Check className="h-6 w-6" /></div>
            <p className="mt-4 text-sm text-white/80">{done}</p>
            {payUrl && (
              <a href={payUrl} target="_blank" rel="noopener noreferrer" data-testid="catalog-pay-link"
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-5 py-2.5 text-sm font-semibold text-emerald-200 transition hover:bg-emerald-500/20">
                <Send className="h-4 w-4" /> Pagar suscripción mensual
              </a>
            )}
            <button type="button" onClick={onClose} className="mt-6 block w-full rounded-full bg-emerald-500 px-6 py-2.5 text-sm font-semibold text-[#0c1220]">Listo</button>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            <input data-testid="catalog-input-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              placeholder="Tu nombre" className="w-full rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-white placeholder:text-white/40 focus:border-emerald-400 focus:outline-none" />
            <input data-testid="catalog-input-wa" value={form.contact_wa} onChange={(e) => setForm({ ...form, contact_wa: e.target.value })}
              placeholder="WhatsApp (ej: +51...)" className="w-full rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-white placeholder:text-white/40 focus:border-emerald-400 focus:outline-none" />
            <input data-testid="catalog-input-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              placeholder="ID de cliente (opcional)" className="w-full rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-white placeholder:text-white/40 focus:border-emerald-400 focus:outline-none" />
            <textarea data-testid="catalog-input-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2}
              placeholder="¿Algo que debamos saber? (opcional)" className="w-full rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-white placeholder:text-white/40 focus:border-emerald-400 focus:outline-none" />
            <button type="button" onClick={submit} disabled={sending} data-testid="catalog-submit-request"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-[#0c1220] transition hover:opacity-90 disabled:opacity-50">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar solicitud
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AgentCard = ({ agent, onRequest }) => (
  <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition hover:border-emerald-400/40 hover:bg-white/[0.05]" data-testid={`catalog-card-${agent.service_id}`}>
    <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-20 blur-2xl transition group-hover:opacity-40" style={{ background: agent.color }} />
    <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${agent.color}22`, color: agent.color }}>
      <Bot className="h-5 w-5" />
    </div>
    <p className="mt-4 text-[10px] uppercase tracking-[0.18em] text-white/40">{agent.sector}</p>
    <h3 className="mt-1 text-lg font-bold text-white">{agent.name}</h3>
    <p className="mt-2 line-clamp-3 text-sm text-white/60">{agent.description || agent.welcome_message}</p>
    <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-white/50">
      <span className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1"><Zap className="h-3 w-3" /> {agent.intents_count} acciones</span>
      <span className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1"><Clock className="h-3 w-3" /> ~{agent.estimated_setup_minutes} min</span>
      {agent.price_pen_month != null && <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/20 bg-emerald-500/10 px-2.5 py-1 text-emerald-200">S/ {agent.price_pen_month}/mes</span>}
    </div>
    <button type="button" onClick={() => onRequest(agent)} data-testid={`catalog-request-${agent.service_id}`}
      className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-[#0c1220] transition hover:opacity-90">
      <Sparkles className="h-4 w-4" /> Instalar con un clic
    </button>
  </div>
);

export default function AgentCatalogPage() {
  const [agents, setAgents] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [sector, setSector] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [modalAgent, setModalAgent] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/studio/catalog`);
        const j = await r.json();
        setAgents(j.agents || []); setSectors(j.sectors || []);
      } catch (e) { /* silent */ } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => sector === 'todos' ? agents : agents.filter((a) => a.sector === sector), [agents, sector]);

  return (
    <div className="min-h-screen bg-[#070b14] pt-28 pb-24" data-testid="agent-catalog-page">
      <SEO title="Catálogo de Agentes IA — Neuralia369" description="Agentes de IA listos para instalar en tu negocio con un clic." />
      <div className="mx-auto max-w-6xl px-6">
        <div className="max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.3em] text-emerald-300">Estudio de servicios · Neuralia369</p>
          <h1 className="mt-3 text-4xl font-bold leading-tight text-white sm:text-5xl">Agentes de IA listos para <span className="text-emerald-300">tu negocio</span></h1>
          <p className="mt-4 text-base text-white/60">Elige un agente publicado por nuestro estudio y pídelo con un clic. Un asesor de Neuralia369 lo configura para ti.</p>
        </div>

        {sectors.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2" data-testid="catalog-filters">
            {['todos', ...sectors].map((s) => (
              <button key={s} type="button" onClick={() => setSector(s)} data-testid={`catalog-filter-${s}`}
                className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition ${sector === s ? 'border-emerald-400 bg-emerald-500/15 text-emerald-200' : 'border-white/10 text-white/50 hover:text-white'}`}>
                {s === 'todos' ? 'Todos' : s}
              </button>
            ))}
          </div>
        )}

        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-grid">
          {loading && <p className="text-white/50" data-testid="catalog-loading">Cargando catálogo…</p>}
          {!loading && filtered.map((a) => <AgentCard key={a.service_id} agent={a} onRequest={setModalAgent} />)}
          {!loading && !filtered.length && (
            <div className="col-span-full rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center" data-testid="catalog-empty">
              <Bot className="mx-auto h-10 w-10 text-white/30" />
              <p className="mt-4 text-sm text-white/60">Aún no hay agentes publicados en el catálogo. Vuelve pronto.</p>
            </div>
          )}
        </div>
      </div>
      {modalAgent && <RequestModal agent={modalAgent} onClose={() => setModalAgent(null)} />}
    </div>
  );
}
